import { useEffect, useState } from 'react';
import { api } from './api.js';

export interface Announcement {
  id: string; title: string; body_md: string;
  level: 'info' | 'update' | 'warning'; published_at: string;
  min_version_code: number | null;
}

/**
 * The banner.
 *
 * Sits above everything so a message written five minutes ago is seen without
 * anybody updating anything. Dismissal is per person and permanent: an
 * announcement you have read should never appear again, which is the difference
 * between this and a notification that nags.
 */
export function AnnouncementBanner(): JSX.Element | null {
  const [items, setItems] = useState<Announcement[]>([]);

  useEffect(() => {
    const load = (): void => {
      void api<Announcement[]>('/announcements').then(setItems).catch(() => undefined);
    };
    load();
    // Five minutes. Often enough that "the box is going down in ten minutes"
    // arrives in time to be useful, rare enough to be invisible.
    const t = window.setInterval(load, 300_000);
    return () => window.clearInterval(t);
  }, []);

  if (items.length === 0) return null;

  const dismiss = (id: string): void => {
    setItems((cur) => cur.filter((i) => i.id !== id));
    void api(`/announcements/${id}/dismiss`, { method: 'POST' }).catch(() => undefined);
  };

  return (
    <>
      {items.map((a) => (
        <div key={a.id} className={`banner banner-${a.level}`}>
          <div>
            <strong>{a.title}</strong>
            <p className="body small">{a.body_md}</p>
          </div>
          <button type="button" className="linkish" onClick={() => dismiss(a.id)}>Dismiss</button>
        </div>
      ))}
    </>
  );
}

/** Publishing, for the owner. Lives on the System screen. */
export function PublishAnnouncement(): JSX.Element {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<'info' | 'update' | 'warning'>('info');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Announcement[]>([]);

  const loadHistory = (): void => {
    void api<Announcement[]>('/announcements/all').then(setHistory).catch(() => undefined);
  };
  useEffect(loadHistory, []);

  async function publish(): Promise<void> {
    setBusy(true); setMsg(null);
    try {
      await api('/announcements', {
        method: 'POST',
        body: JSON.stringify({ title, bodyMd: body, level }),
      });
      setTitle(''); setBody(''); setMsg('Published. It is live for everyone now.');
      loadHistory();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <h2>Push an update</h2>
      <p className="sub">
        Goes to everyone immediately, in the app and on the phone. No new build needed.
      </p>
      <label htmlFor="atitle">Title</label>
      <input id="atitle" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What changed" />
      <label htmlFor="abody">Message</label>
      <textarea id="abody" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
      <label htmlFor="alevel">Kind</label>
      <select id="alevel" value={level} onChange={(e) => setLevel(e.target.value as 'info')}>
        <option value="info">Information</option>
        <option value="update">New in this release</option>
        <option value="warning">Warning</option>
      </select>
      <button type="button" disabled={busy || title.length < 2 || body.length < 2} onClick={() => void publish()}>
        {busy ? 'Publishing…' : 'Publish to everyone'}
      </button>
      {msg && <p className="muted small">{msg}</p>}

      {history.length > 0 && (
        <>
          <h3 className="subhead">Published before</h3>
          <ul className="plain">
            {history.slice(0, 8).map((a) => (
              <li key={a.id}>
                <strong>{a.title}</strong>
                <span className="muted small"> · {new Date(a.published_at).toLocaleDateString('en-ZA')}</span>
                <button type="button" className="linkish danger"
                        onClick={() => { void api(`/announcements/${a.id}`, { method: 'DELETE' }).then(loadHistory); }}>
                  delete
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
